import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { MessageDirection, MessageNotificationDTO } from "@/hooks/types"
import { useWebSocketCallback } from "@/hooks/use-websocket-channel"
import { useLocation, useNavigate } from "react-router-dom"
import { toast } from "sonner"

import { useChatStore } from "../../../features/chat/hooks/use-chat-store"

interface ActivityFeedNotificationDTO {
  id: string
  organization_id: string
  user_id: string | null
  timestamp: Date
  activity_type: string
  title: string
  description: string
  source_module: string
  related_entity_id: string | null
  related_entity_type: string | null
  metadata: Record<string, any> | null
  is_read: boolean
}

const getInitials = (name: string) => {
  const nameParts = name.split(" ")
  if (nameParts.length === 1) return nameParts[0].substring(0, 2).toUpperCase()
  return (nameParts[0][0] + nameParts[nameParts.length - 1][0]).toUpperCase()
}

/**
 * Componente que escuta eventos do Ably e exibe notificações de toast
 * Deve ser incluído apenas uma vez na aplicação, geralmente no componente raiz
 */
export function NotificationsToastListener() {
  const { fetchConversation } = useChatStore()
  const location = useLocation()
  const navigate = useNavigate()

  const isInChatPage = location.pathname.startsWith("/chat")

  useWebSocketCallback("message.new", (data: MessageNotificationDTO) => {
    return // skip for awhile
    handleNewMessage(data, async () => {
      if (!isInChatPage) {
        const conversation = await fetchConversation(data.conversation.id)
        navigate(`/chat/${data.conversation.id}?tab=${conversation.status}`)
      }
    })
  })

  useWebSocketCallback("lead.created", (data: ActivityFeedNotificationDTO) => {
    handleNewLead(data, async () => {
      navigate(`/contacts/${data.related_entity_id}`)
    })
  })

  useWebSocketCallback("appointment.created", (data: ActivityFeedNotificationDTO) => {
    handleAppointment(data)
  })
  useWebSocketCallback("appointment.rescheduled", (data: ActivityFeedNotificationDTO) => {
    handleAppointment(data)
  })
  useWebSocketCallback("appointment.confirmed", (data: ActivityFeedNotificationDTO) => {
    handleAppointment(data)
  })
  useWebSocketCallback("appointment.cancelled", (data: ActivityFeedNotificationDTO) => {
    handleAppointment(data)
  })
  useWebSocketCallback("appointment.awaiting_payment", (data: ActivityFeedNotificationDTO) => {
    handleAppointment(data)
  })
  useWebSocketCallback("appointment.paid", (data: ActivityFeedNotificationDTO) => {
    handleAppointment(data)
  })

  // Este componente não renderiza nada na UI
  return null
}

function handleNewMessage(data: MessageNotificationDTO, onClick: () => void) {
  if (data.message.direction === MessageDirection.OUTBOUND) return

  toast.success(`Nova mensagem de ${data.conversation.contact_name}`, {
    id: "message-new",
    description:
      (data.message.content as { text: string }).text ||
      (data.message.content as string) ||
      "Nova mensagem recebida",
    duration: 5000,
    action: {
      label: "Ver",
      onClick: onClick,
    },
    icon: data.conversation.contact_image_url ? (
      <Avatar className="size-6">
        <AvatarImage
          src={data.conversation.contact_image_url}
          alt={data.conversation.contact_name || "Desconhecido"}
        />
        <AvatarFallback className="rounded-lg">
          {getInitials(data.conversation.contact_name || "Desconhecido")}
        </AvatarFallback>
      </Avatar>
    ) : undefined,
  })
}

function handleNewLead(activity: ActivityFeedNotificationDTO, onClick: () => void) {
  toast.success(activity.title, {
    id: "lead-new",
    description: activity.description,
    duration: 5000,
    action: {
      label: "Ver",
      onClick: onClick,
    },
    icon: activity.metadata?.client_photo_url ? (
      <Avatar className="size-6">
        <AvatarImage
          src={activity.metadata.client_photo_url}
          alt={activity.metadata?.client_name || "-"}
        />
        <AvatarFallback className="rounded-lg">
          {getInitials(activity.metadata?.client_name || "-")}
        </AvatarFallback>
      </Avatar>
    ) : undefined,
  })
}

function handleAppointment(activity: ActivityFeedNotificationDTO) {
  const toastFn =
    activity.activity_type === "appointment.cancelled"
      ? toast.error
      : activity.activity_type === "appointment.rescheduled"
        ? toast.info
        : toast.success
  toastFn(activity.title, {
    id: "appointment-new",
    description: activity.description,
    duration: 5000,
    icon: activity.metadata?.client_photo_url ? (
      <Avatar className="size-6">
        <AvatarImage
          src={activity.metadata.client_photo_url}
          alt={activity.metadata?.client_name || "-"}
        />
        <AvatarFallback className="rounded-lg">
          {getInitials(activity.metadata?.client_name || "-")}
        </AvatarFallback>
      </Avatar>
    ) : undefined,
  })
}
